import React from 'react';
import type { NewCoreIconProps } from '@atlaskit/icon';
import ArchiveBoxIcon from '@atlaskit/icon/core/archive-box';
import FormIcon from '@atlaskit/icon/core/form';
import GlobeIcon from '@atlaskit/icon/core/globe';
import AttachmentsIcon from '@atlaskit/icon/core/migration/attachment';
import BoardIcon from '@atlaskit/icon/core/migration/board';
import CalendarIcon from '@atlaskit/icon/core/migration/calendar';
import ChartTrendIcon from '@atlaskit/icon/core/migration/chart-trend--graph-line';
import ComponentsIcon from '@atlaskit/icon/core/migration/component';
import ActivityIconLegacy from '@atlaskit/icon/glyph/activity';
import DecisionIcon from '@atlaskit/icon/core/migration/decision--editor-decision';
import IssuesIcon from '@atlaskit/icon/core/migration/issues';
import LinkIcon from '@atlaskit/icon/core/migration/link--editor-link';
import BulletListIconLegacy from '@atlaskit/icon/glyph/bullet-list';
import RoadmapIcon from '@atlaskit/icon/core/migration/roadmap';
import PageIcon from '@atlaskit/icon/core/page';
import SpreadsheetIcon from '@atlaskit/icon/core/spreadsheet';
import MediaWrapLeftIcon from '@atlaskit/icon/glyph/editor/media-wrap-left';
import FileIcon from '@atlaskit/icon/glyph/file';
import {
	SUMMARY_ROUTES,
	BOARD_ROUTES,
	LIST_ROUTES,
	TIMELINE_ROUTES,
	CALENDAR_ROUTES,
	FORMS_ROUTES,
	APPROVALS_ROUTES,
	ATTACHMENTS_ROUTES,
	PAGES_ROUTES,
	ISSUES_ROUTES,
	REPORTS_ROUTES,
	COMPONENTS_ROUTES,
	SHORTCUTS_ROUTES,
	ARCHIVE_ROUTES,
} from '@atlassian/jira-business-navigation-common/src/index.tsx';
import {
	SummaryDetails,
	BoardDetails,
	ListDetails,
	TimelineDetails,
	CalendarDetails,
	FormsDetails,
	ApprovalsDetails,
	AttachmentsDetails,
	PagesDetails,
	IssuesDetails,
	ReportsDetails,
	ComponentsDetails,
	ShortcutsDetails,
	AppDetails,
	ArchivedIssuesDetails,
} from '@atlassian/jira-business-tabs/src/ui/add-tab-details/index.tsx';
import AppPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/app-preview/index.tsx';
import ApprovalsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/approvals-preview/index.tsx';
import AttachmentsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/attachments-preview/index.tsx';
import BoardPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/board-preview/index.tsx';
import CalendarPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/calendar-preview/index.tsx';
import ComponentPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/component-preview/index.tsx';
import FormsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/forms-preview/index.tsx';
import IssuesPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/issues-preview/index.tsx';
import ListPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/list-preview/index.tsx';
import PagesPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/pages-preview/index.tsx';
import ReportsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/reports-preview/index.tsx';
import ShortcutsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/shortcuts-preview/index.tsx';
import SummaryPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/summary-preview/index.tsx';
import TimelinePreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/timeline-preview/index.tsx';
import type { TabsConfiguration } from '@atlassian/jira-navigation-kit-common/src/common/tabs-configuration-context/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

export const tabsIcons = {
	SUMMARY: (props: NewCoreIconProps) => (
		<GlobeIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
			LEGACY_fallbackIcon={ActivityIconLegacy}
		/>
	),
	BOARD: (props: NewCoreIconProps) => (
		<BoardIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
		/>
	),
	LIST: (props: NewCoreIconProps) => (
		<SpreadsheetIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
			LEGACY_fallbackIcon={BulletListIconLegacy}
		/>
	),
	TIMELINE: (props: NewCoreIconProps) => (
		<RoadmapIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
		/>
	),
	CALENDAR: (props: NewCoreIconProps) => (
		<CalendarIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
		/>
	),
	FORMS: (props: NewCoreIconProps) => (
		<FormIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
			LEGACY_fallbackIcon={MediaWrapLeftIcon}
		/>
	),
	APPROVALS: (props: NewCoreIconProps) => (
		<DecisionIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
		/>
	),
	ATTACHMENTS: (props: NewCoreIconProps) => (
		<AttachmentsIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
		/>
	),
	PAGES: (props: NewCoreIconProps) => (
		<PageIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
			LEGACY_fallbackIcon={FileIcon}
		/>
	),
	ISSUES: (props: NewCoreIconProps) => (
		<IssuesIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
		/>
	),
	REPORTS: (props: NewCoreIconProps) => (
		<ChartTrendIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
		/>
	),
	COMPONENTS: (props: NewCoreIconProps) => (
		<ComponentsIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
		/>
	),
	SHORTCUTS: (props: NewCoreIconProps) => (
		<LinkIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
		/>
	),
	ARCHIVED_ISSUES: (props: NewCoreIconProps) => (
		<ArchiveBoxIcon
			{...props}
			color="currentColor"
			spacing={props.size === 'small' ? 'none' : 'spacious'}
			LEGACY_size={props.size}
		/>
	),
};

export const tabsConfiguration: TabsConfiguration = {
	SUMMARY: {
		type: 'project',
		id: 'summaryTab',
		team: 'Wanjel',
		getConfiguration: () => ({
			path: '/summary',
			icon: tabsIcons.SUMMARY,
			isSelectedOnRoutes: SUMMARY_ROUTES,
		}),
		tabDetails: {
			illustration: <SummaryPreview alt="" />,
			component: SummaryDetails,
		},
	},
	BOARD: {
		type: 'project',
		id: 'boardTab',
		team: 'Wanjel',
		getConfiguration: () => ({
			path: '/board',
			icon: tabsIcons.BOARD,
			isSelectedOnRoutes: BOARD_ROUTES,
		}),
		tabDetails: {
			illustration: <BoardPreview alt="" />,
			component: BoardDetails,
		},
	},
	LIST: {
		type: 'project',
		id: 'listTab',
		team: 'Wanjel',
		getConfiguration: () => ({
			path: '/list',
			icon: tabsIcons.LIST,
			isSelectedOnRoutes: LIST_ROUTES,
		}),
		tabDetails: {
			illustration: <ListPreview alt="" />,
			component: ListDetails,
		},
	},
	TIMELINE: {
		type: 'project',
		id: 'timelineTab',
		team: 'Wanjel',
		getConfiguration: () => ({
			path: '/timeline',
			icon: tabsIcons.TIMELINE,
			isSelectedOnRoutes: TIMELINE_ROUTES,
		}),
		tabDetails: {
			illustration: <TimelinePreview alt="" />,
			component: TimelineDetails,
		},
	},
	CALENDAR: {
		type: 'project',
		id: 'calendarTab',
		team: 'Wanjel',
		getConfiguration: () => ({
			path: '/calendar',
			icon: tabsIcons.CALENDAR,
			isSelectedOnRoutes: CALENDAR_ROUTES,
		}),
		tabDetails: {
			illustration: <CalendarPreview alt="" />,
			component: CalendarDetails,
		},
	},
	FORMS: {
		type: 'project',
		id: 'formsTab',
		team: 'Wanjel',
		getConfiguration: () => ({
			path: '/form',
			icon: tabsIcons.FORMS,
			isSelectedOnRoutes: FORMS_ROUTES,
		}),
		tabDetails: {
			illustration: <FormsPreview alt="" />,
			component: FormsDetails,
		},
	},
	APPROVALS: {
		type: 'project',
		id: 'approvalsTab',
		team: 'Wanjel',
		getConfiguration: () => ({
			path: '/approvals',
			icon: tabsIcons.APPROVALS,
			isSelectedOnRoutes: APPROVALS_ROUTES,
		}),
		tabDetails: {
			illustration: <ApprovalsPreview alt="" />,
			component: ApprovalsDetails,
		},
	},
	ATTACHMENTS: {
		type: 'project',
		id: 'attachmentsTab',
		team: 'Wanjel',
		getConfiguration: () => ({
			path: '/attachments',
			icon: tabsIcons.ATTACHMENTS,
			isSelectedOnRoutes: ATTACHMENTS_ROUTES,
		}),
		tabDetails: {
			illustration: <AttachmentsPreview alt="" />,
			component: AttachmentsDetails,
		},
	},
	PAGES: {
		type: 'project',
		id: 'pagesTab',
		team: 'Wanjel',
		getConfiguration: () => ({
			path: '/pages',
			icon: tabsIcons.PAGES,
			isSelectedOnRoutes: PAGES_ROUTES,
		}),
		tabDetails: {
			illustration: <PagesPreview alt="" />,
			component: PagesDetails,
		},
	},
	ISSUES: {
		type: 'project',
		id: 'issuesTab',
		team: 'Wanjel',
		getConfiguration: () => ({
			path: '/issues',
			icon: tabsIcons.ISSUES,
			isSelectedOnRoutes: ISSUES_ROUTES,
		}),
		tabDetails: {
			illustration: <IssuesPreview alt="" />,
			component: IssuesDetails,
		},
	},
	REPORTS: {
		type: 'reports',
		id: 'reportsTab',
		team: 'Wanjel',
		getConfiguration: () => ({
			icon: tabsIcons.REPORTS,
			isSelectedOnRoutes: REPORTS_ROUTES,
		}),
		tabDetails: {
			illustration: <ReportsPreview alt="" />,
			component: ReportsDetails,
		},
	},
	COMPONENTS: {
		type: 'project',
		id: 'componentsTab',
		team: 'Wanjel',
		getConfiguration: () => ({
			path: '/components',
			icon: tabsIcons.COMPONENTS,
			isSelectedOnRoutes: COMPONENTS_ROUTES,
		}),
		tabDetails: {
			illustration: <ComponentPreview alt="" />,
			component: ComponentsDetails,
		},
	},
	SHORTCUTS: {
		type: 'shortcuts',
		id: 'shortcutsTab',
		team: 'Wanjel',
		getConfiguration: () => ({
			icon: tabsIcons.SHORTCUTS,
			isSelectedOnRoutes: SHORTCUTS_ROUTES,
		}),
		tabDetails: {
			illustration: <ShortcutsPreview alt="" />,
			component: ShortcutsDetails,
		},
	},
	APP: {
		type: 'app',
		id: 'appTab',
		team: 'Wanjel',
		getConfiguration: () => ({}),
		tabDetails: {
			illustration: <AppPreview alt="" />,
			component: AppDetails,
		},
	},
	ARCHIVED_ISSUES: {
		type: 'project',
		id: 'archivedIssuesTab',
		team: 'Kenji',
		getConfiguration: () => ({
			path: fg('slv-3_migrate_archived_issues_routes') ? '/archived-work-items' : '/issues/archive',
			icon: tabsIcons.ARCHIVED_ISSUES,
			isSelectedOnRoutes: ARCHIVE_ROUTES,
		}),
		tabDetails: {
			illustration: <IssuesPreview alt="" />,
			component: ArchivedIssuesDetails,
		},
	},
};
